/**
 * The two "what you can do" boxes at the top of /what-we-offer: wildlife and outdoor adventure.
 * Each box leads with a looping video; its poster is a real operator cover (Cloudinary).
 * Live operator counts are an optional extra: if the API fails the boxes render without them.
 */

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Mountain, PawPrint, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CatalogueStats } from './useCatalogueStats';

/** Ask Cloudinary for a right-sized, auto-format crop instead of the full-size original. */
const sized = (src: string, w: number, h: number) =>
  src.replace('/image/upload/', `/image/upload/f_auto,q_auto,c_fill,g_auto,w_${w},h_${h}/`);

const CLD = 'https://res.cloudinary.com/dlj3oqydo/image/upload';

// Each box leads with its video. The poster is a real operator cover (business-images skill):
// it shows while the video loads and replaces it for reduced-motion users.
const BOXES: { key: 'wildlife' | 'adventure'; icon: LucideIcon; video: string; poster: string }[] = [
  {
    key: 'wildlife',
    icon: PawPrint,
    video: '/video/what_we_offer/animales-optimized.mp4',
    poster: `${CLD}/v1793440277/wildy/arctic-whale-tours-andenes/arctic-whale-tours-andenes-cover.jpg`,
  },
  {
    key: 'adventure',
    icon: Mountain,
    video: '/video/what_we_offer/aventura-optimized.mp4',
    poster: `${CLD}/v1793440255/wildy/mad-goats-sjoa/mad-goats-sjoa-cover.jpg`,
  },
];

/**
 * Decorative looping video. The files are large (16-20 MB), so nothing downloads until the box
 * is near the viewport, and it pauses when scrolled away.
 */
function ExperienceVideo({ src, poster }: { src: string; poster: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (video.preload !== 'auto') { video.preload = 'auto'; video.load(); }
        video.play().catch(() => { /* autoplay blocked: the poster stays */ });
      } else {
        video.pause();
      }
    }, { rootMargin: '200px 0px' });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const posterSrc = sized(poster, 960, 540);
  return (
    <div className="relative aspect-video bg-brand-900 overflow-hidden" aria-hidden>
      <video
        ref={ref}
        src={src}
        poster={posterSrc}
        muted
        loop
        playsInline
        preload="none"
        className="absolute inset-0 h-full w-full object-cover motion-reduce:hidden"
      />
      <img src={posterSrc} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover hidden motion-reduce:block" />
    </div>
  );
}

export function ExperienceBoxes({ stats }: { stats: CatalogueStats | null }) {
  const { t } = useTranslation('public', { keyPrefix: 'offerings.boxes' });
  const navigate = useNavigate();

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {BOXES.map(({ key, icon: Icon, video, poster }, i) => {
        const examples = t(`${key}.examples`, { returnObjects: true });
        const count = stats ? (key === 'wildlife' ? stats.wildlife : stats.adventure) : 0;
        return (
          <motion.article
            key={key}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="group flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow"
          >
            <ExperienceVideo src={video} poster={poster} />
            <div className="flex flex-1 flex-col p-6 sm:p-8">
              <div className="flex items-center justify-between gap-3 mb-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-primary">
                  <Icon className="w-5 h-5" aria-hidden />
                </span>
                {count > 0 && (
                  <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-800">
                    {t('count', { count })}
                  </span>
                )}
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-brand-900 tracking-tight">{t(`${key}.title`)}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t(`${key}.description`)}</p>
              <ul className="mt-5 flex flex-wrap gap-2" aria-label={t('examplesLabel')}>
                {(Array.isArray(examples) ? examples as string[] : []).map(ex => (
                  <li key={ex} className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-sm text-brand-800">{ex}</li>
                ))}
              </ul>
              <div className="mt-auto pt-7">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="group/cta w-full sm:w-auto border-primary text-primary hover:bg-brand-50 hover:text-brand-700"
                >
                  {t(`${key}.cta`)}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover/cta:translate-x-1 transition-transform" aria-hidden />
                </Button>
              </div>
            </div>
          </motion.article>
        );
      })}
    </div>
  );
}
